// The clip library — every distinct piece of audio a seed is built from.
//
// A seed's arrangement is mostly repetition: in a typical Set, 77 clips are 13
// actual sounds, one of which is a kick placed 40 times. Listing all 77 buries
// the material under its own repetition, so clips are grouped by what makes
// them *sound* different — the source region and length, not their position —
// and every individual placement stays reachable underneath.
//
// Grouping is per TRACK as well as per clip, because a seed plays a clip as a
// window into that track's stem: the same sample on two tracks comes out of two
// different renders and is genuinely two entries.
//
// It is also per SLOT, which is the one place this stops reducing. Repetition
// on the timeline is a by-product of arranging — nobody decided to place that
// kick forty times, they decided on a groove — but a Session View clip is a
// thing the author put in a slot on purpose. The layout IS the intent, and a
// duplicate in it is as deliberate as anything else: the same loop in two
// slots is two pads to hit, not one pad counted twice. So slot clips are
// listed as synced, never folded — not into each other, and not into a
// timeline clip that happens to sound the same.

import { beatsToSeconds, type ClipSource, type MidiSource } from "@web-shared/lib/audio-engine";
import { objectUrl } from "@web-shared/lib/api";
import { midiNotesToSource, objectPlayback, objectTiming, warpOf } from "@web-shared/lib/player-sources";
import { roomClipGeometry, sessionGeometry, timelineGeometry } from "@web-shared/lib/arrangement";
import { materialKey, roomClipFacts, variantKey, r4 } from "@web-shared/lib/material";
import type { MidiStage } from "@web-shared/lib/midi-effects";
import type { AudioClipInfo, SeedDetail, Snapshot } from "@web-shared/lib/types";
import type { MidiClipInfo, SessionAudioClipInfo } from "@shared/seed-history.js";
import type { CanonicalClipState, CanonicalTrackState } from "@shared/sync/state.js";

export interface LibraryOccurrence {
  /** Position on the timeline, in beats (0 for a Session View clip). */
  start: number;
  /** The room's own id for this placement. Present only on a library built
   * from a live room (`buildRoomClipLibrary`) — a seed's snapshot has no ids
   * to give. It is what a drag off the shelf carries, so a drop can copy the
   * actual clip rather than reconstructing one from its description. */
  clipId?: string;
  /** Session View clips: the clip slot this occurrence sits in. */
  slot?: number;
  /** Playable window into the track's stem; null when the track has no stem. */
  source: ClipSource | null;
  /** MIDI clips: the synthesisable notes (sine preview). A bounced MIDI clip
   * carries `source` too, and the card prefers the real audio. */
  midi?: MidiSource | null;
}

export interface LibraryClip {
  key: string;
  trackName: string;
  trackIndex: number;
  name: string;
  /** Basename of the clip's source file — the actual sample name. */
  sourceFile: string | null;
  /** Length in beats, and at the seed's tempo, in seconds. */
  durationBeats: number;
  durationSec: number;
  startMarker: number;
  endMarker: number;
  color: number;
  muted: boolean;
  warped: boolean;
  warpMode: string;
  looping: boolean;
  /** Absent ⇒ audio, matching LaneClip. */
  kind?: "audio" | "midi";
  /** MIDI clips: how many notes — the count survives even when the notes were
   * omitted for size and the clip cannot be previewed. */
  noteCount?: number;
  occurrences: LibraryOccurrence[];
}

export interface LibraryTrack {
  trackIndex: number;
  name: string;
  /** False when no stem matched this track — its clips cannot be auditioned. */
  playable: boolean;
  clips: LibraryClip[];
  /** Total placements across the track, i.e. before grouping. */
  placements: number;
}

export interface ClipLibrary {
  tracks: LibraryTrack[];
  distinctClips: number;
  totalPlacements: number;
  /** Tracks holding clips we have no audio for, so the UI can say why. */
  unplayableTracks: string[];
}

/** Windows-and-POSIX-safe basename. Live Sets travel between both. */
function basename(path: string): string | null {
  if (!path) return null;
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] || null;
}

/**
 * A Session View clip's own entry, and nothing else's.
 *
 * Appended to every grouping key in this module, so a slot clip can only ever
 * share an entry with itself. NOT part of `materialKey`, which answers a
 * different question — "would these sound the same" — and is what
 * `ghost-clips.ts` asks to decide that a timeline loop already sits in a slot
 * and needs no ghost of itself. Folding this into that rule would put both on
 * the grid.
 */
function slotKey(slot: number | undefined): string {
  return slot === undefined ? "" : `|slot${slot}`;
}

/**
 * Two clips are the same library entry when they'd sound the same — the one
 * rule in `lib/material.ts`, scoped to the track because a seed plays a clip
 * as a window into THAT track's render and the same sample on two tracks is
 * genuinely two entries. Position is deliberately absent: that is what makes
 * 40 kicks one entry with 40 placements. A slot is not (`slotKey`).
 */
function groupKey(
  trackIndex: number,
  c: Pick<AudioClipInfo, "name" | "duration" | "startMarker" | "endMarker" | "looping"> & { warping?: boolean },
): string {
  return `${trackIndex}|${materialKey({
    name: c.name,
    duration: c.duration,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    looping: c.looping,
    warped: c.warping,
  })}`;
}

export function buildClipLibrary(
  snapshot: Snapshot,
  detail: SeedDetail,
  bpm: number,
  stemUrlByTrackName: Map<string, string>,
): ClipLibrary {
  const tracks: LibraryTrack[] = [];
  const unplayable: string[] = [];
  let distinct = 0;
  let placements = 0;

  snapshot.audioTracks.forEach((track, trackIndex) => {
    // Session View clips are material too, listed beside the timeline's and
    // each kept to its own slot (`slotKey`).
    // Slot clips carry their own `slot` and (on a full-mode capture) Live's
    // warp markers — both are what `sessionGeometry` reads to describe a
    // whole-file object's playback.
    const allClips: Array<AudioClipInfo & Partial<Pick<SessionAudioClipInfo, "slot" | "warpMarkers">>> = [
      ...track.arrangementClips,
      ...(track.sessionClips ?? []),
    ];
    if (allClips.length === 0) return;

    const stem = stemUrlByTrackName.get(track.name);
    // Unplayable only when the track has neither a stem NOR per-clip audio.
    const anyClipAudio = allClips.some((c) => Boolean(c.audioKey));
    if (!stem && !anyClipAudio) unplayable.push(track.name);

    const grouped = new Map<string, LibraryClip>();
    for (const clip of allClips) {
      const key = `${groupKey(trackIndex, clip)}${slotKey(clip.slot)}`;
      // Unique per placement so the player can report which one is live.
      const id = `lib:${key}:${clip.slot !== undefined ? `slot${clip.slot}` : r4(clip.startTime)}`;
      const laneId = `lib:${trackIndex}`;
      // A TIMELINE clip that owns its audio plays that object whole: the
      // object is its flattened region render, so trim, loop and warp are
      // already in the bytes. A SLOT clip's object is its source file whole
      // (upload/clip-audio.ts — a slot has no region to render), so its
      // markers are the only description of what Live plays and the card has
      // to apply them, exactly as the grid's click-to-play does. Without that
      // a card played the file from second 0, unlooped, at its own speed.
      //
      // The offset window below is only for stem-shaped seeds — and it is the
      // shape that can go silent, because a clip sitting past the end of its
      // render asks for an offset beyond the buffer.
      const source: ClipSource | null = clip.audioKey
        ? {
            id,
            laneId,
            url: objectUrl(clip.audioKey),
            startSec: 0,
            ...objectPlayback(
              clip.slot !== undefined ? sessionGeometry(clip, snapshot.song.tempo) : timelineGeometry(clip, snapshot.song.tempo),
              clip.duration,
              bpm,
            ),
          }
        : stem && clip.slot === undefined // a stem is a timeline render; it never covers a slot
          ? {
              id,
              laneId,
              url: stem,
              startSec: 0,
              offsetSec: beatsToSeconds(clip.startTime, bpm),
              durationSec: beatsToSeconds(clip.duration, bpm),
            }
          : null;
      const occurrence: LibraryOccurrence = {
        start: clip.startTime,
        source,
        ...(clip.slot !== undefined ? { slot: clip.slot } : {}),
      };

      const existing = grouped.get(key);
      if (existing) {
        existing.occurrences.push(occurrence);
        continue;
      }
      grouped.set(key, {
        key,
        trackName: track.name,
        trackIndex,
        name: clip.name,
        sourceFile: basename(clip.filePath),
        durationBeats: clip.duration,
        durationSec: beatsToSeconds(clip.duration, bpm),
        startMarker: clip.startMarker,
        endMarker: clip.endMarker,
        color: clip.color,
        muted: clip.muted,
        warped: clip.warping,
        warpMode: clip.warpMode.name,
        looping: clip.looping,
        occurrences: [occurrence],
      });
    }

    const clips = [...grouped.values()]
      .map((c) => ({ ...c, occurrences: [...c.occurrences].sort((a, b) => a.start - b.start) }))
      .sort((a, b) => (a.occurrences[0]?.start ?? 0) - (b.occurrences[0]?.start ?? 0));

    distinct += clips.length;
    placements += allClips.length;
    tracks.push({
      trackIndex,
      name: track.name,
      playable: Boolean(stem),
      clips,
      placements: allClips.length,
    });
  });

  // MIDI lanes, indexed after the audio ones — same order the arrangement
  // draws them. Identity is the notes digest + length: two placements that play
  // the same notes are one entry, whatever they were renamed to.
  (snapshot.midiTracks ?? []).forEach((track, i) => {
    const allClips: Array<MidiClipInfo & { slot?: number }> = [
      ...track.arrangementClips,
      ...(track.sessionClips ?? []),
    ];
    if (allClips.length === 0) return;
    const trackIndex = snapshot.audioTracks.length + i;
    const laneId = `lib:midi:${i}`;

    const anyAudible = allClips.some(
      (c) => Boolean(c.audioKey) || c.notes.some((n) => !n.muted),
    );
    if (!anyAudible) unplayable.push(track.name);

    const grouped = new Map<string, LibraryClip>();
    for (const clip of allClips) {
      const key = `${trackIndex}|midi|${clip.notesDigest}|${r4(clip.duration)}${slotKey(clip.slot)}`;
      const id = `lib:${key}:${clip.slot !== undefined ? `slot${clip.slot}` : r4(clip.startTime)}`;
      const durationSec = beatsToSeconds(clip.duration, bpm);
      // A bounce, if one ever exists, is the truer sound; the sine preview is
      // the fallback that always works.
      const source: ClipSource | null = clip.audioKey
        ? { id, laneId, url: objectUrl(clip.audioKey), startSec: 0, durationSec }
        : null;
      // Never both: a bounced clip is its bounce (`clipSounding`).
      const midi = source ? null : midiNotesToSource(clip.notes, bpm, { id, laneId, startSec: 0, durationSec });
      const occurrence: LibraryOccurrence = {
        start: clip.startTime,
        source,
        midi,
        ...(clip.slot !== undefined ? { slot: clip.slot } : {}),
      };

      const existing = grouped.get(key);
      if (existing) {
        existing.occurrences.push(occurrence);
        continue;
      }
      grouped.set(key, {
        key,
        trackName: track.name,
        trackIndex,
        name: clip.name,
        sourceFile: null,
        durationBeats: clip.duration,
        durationSec,
        startMarker: clip.startMarker,
        endMarker: clip.endMarker,
        color: clip.color,
        muted: clip.muted,
        warped: false,
        warpMode: "",
        looping: clip.looping,
        kind: "midi",
        noteCount: clip.noteCount,
        occurrences: [occurrence],
      });
    }

    const clips = [...grouped.values()]
      .map((c) => ({ ...c, occurrences: [...c.occurrences].sort((a, b) => a.start - b.start) }))
      .sort((a, b) => (a.occurrences[0]?.start ?? 0) - (b.occurrences[0]?.start ?? 0));

    distinct += clips.length;
    placements += allClips.length;
    tracks.push({
      trackIndex,
      name: track.name,
      playable: anyAudible,
      clips,
      placements: allClips.length,
    });
  });

  void detail;
  return {
    tracks,
    distinctClips: distinct,
    totalPlacements: placements,
    unplayableTracks: unplayable,
  };
}

/** Whole-master fallback URL, for seeds that have no per-track stems at all. */
export function masterUrl(detail: SeedDetail): string | null {
  return detail.masterKey ? objectUrl(detail.masterKey) : null;
}

/**
 * The same library, built from a live room's canonical state.
 *
 * A room is the easy shape: every canonical clip carries its own R2 audioKey,
 * so a clip plays from the top of its own object — no stem windows. That key
 * IS the sound's identity (content-addressed bytes), so grouping keys on it
 * when present: two placements of the same object are one entry even after a
 * rename, and two same-named clips backed by different bytes stay two entries.
 * A clip with no key yet (its upload never landed) falls back to the seed-side
 * looks-the-same key, and its occurrences are unplayable rather than hidden.
 *
 * Grouping stays per TRACK for the same reason the seed's does — the card
 * names its track, and a cross-track merge would have to lie about that.
 */
export function buildRoomClipLibrary(
  roomTracks: CanonicalTrackState[],
  roomClips: CanonicalClipState[],
  bpm: number,
  /** Each lane's MIDI effects (midi-effects.ts), by track id — a preview hears them as the lane does. */
  effectsByTrack: Record<string, readonly MidiStage[]> = {},
  /** Where a clip's object is fetched from. The admin app reads R2 through
   * `objectUrl`; the account room has no admin token and reads the public
   * blob endpoint instead — same shape `roomSourcesWithUrls` takes. A key
   * this cannot resolve leaves the entry unplayable rather than broken. */
  urlOf: (audioKey: string) => string | undefined = objectUrl,
  /** A fingerprint of an object's actual audio, where one has been resolved
   * (hooks/useMaterialVariants.ts). Only objects that collide on their
   * description ever get one; it is what keeps a transposed duplicate off the
   * same card as its original. */
  variantOf: (audioKey: string) => string | undefined = () => undefined,
): ClipLibrary {
  const tracks: LibraryTrack[] = [];
  const unplayable: string[] = [];
  let distinct = 0;
  let placements = 0;

  const byTrack = new Map<string, CanonicalClipState[]>();
  for (const c of roomClips) {
    byTrack.set(c.trackId, [...(byTrack.get(c.trackId) ?? []), c]);
  }

  // `ord` is display order only, never identity — same rule as fromRoom.
  [...roomTracks]
    .sort((a, b) => a.ord - b.ord)
    .forEach((track, trackIndex) => {
      const clips = (byTrack.get(track.id) ?? []).sort((a, b) => a.startTime - b.startTime);
      if (clips.length === 0) return;

      // A MIDI clip needs an audible note (or a bounce), an audio clip its key.
      const canSound = (c: CanonicalClipState): boolean =>
        c.clipKind === "midi"
          ? Boolean(c.audioKey) || (c.notes ?? []).some((n) => !n.muted)
          : Boolean(c.audioKey);
      if (clips.some((c) => !canSound(c))) unplayable.push(track.name);

      const grouped = new Map<string, LibraryClip>();
      for (const clip of clips) {
        const isMidi = clip.clipKind === "midi";
        // NOT the object on its own. Live fades a clip's edges and the fade
        // depends on what the clip abuts, so one loop placed five times can
        // render to three different hashes — three cards, three pads, for one
        // loop (lib/material.ts has the measurement). The description is what
        // is stable; a variant fingerprint splits it again where two clips
        // describe themselves alike but genuinely sound different.
        const key = `${trackIndex}|${variantKey(
          roomClipFacts(clip),
          clip.audioKey ? variantOf(clip.audioKey) : undefined,
        )}${slotKey(clip.slot)}`;
        // The clip's room id: unique per placement, and the same id the
        // arrangement view auditions under a `audition:` prefix — so the
        // two tabs never fight over which occurrence is live.
        const id = `lib:${clip.id}`;
        const laneId = `lib:${track.id}`;
        const durationSec = beatsToSeconds(clip.duration, bpm);
        const url = clip.audioKey ? urlOf(clip.audioKey) : undefined;
        const source: ClipSource | null = url
          ? {
              id,
              laneId,
              url,
              startSec: 0,
              // A SLOT clip's object is its source file whole, so the card
              // has to play it through the clip's markers and loop brace —
              // the same reading `clipAudition` gives it on the grid. A
              // timeline clip's object is a flattened render, which needs
              // nothing but its length.
              ...(clip.slot !== undefined
                ? objectPlayback(roomClipGeometry(clip), clip.duration, bpm)
                : {
                    durationSec,
                    // A warped clip at another tempo previews stretched onto
                    // the room's grid, as it plays on the timeline.
                    ...warpOf(objectTiming({ isWarped: clip.isWarped, sourceTempo: clip.sourceTempo, warpMode: clip.warpModeValue, warpMarkers: clip.warpMarkers, transpose: clip.transpose, detune: clip.detune, warpSettings: clip.warpSettings }, bpm)),
                  }),
            }
          : null;
        // Never both: a bounced clip is its bounce (`clipSounding`).
        const midi =
          isMidi && !source
            ? midiNotesToSource(clip.notes, bpm, { id, laneId, startSec: 0, durationSec }, Math.random, effectsByTrack[track.id] ?? [])
            : null;
        const occurrence: LibraryOccurrence = {
          start: clip.startTime,
          clipId: clip.id,
          source,
          midi,
          ...(clip.slot !== undefined ? { slot: clip.slot } : {}),
        };

        const existing = grouped.get(key);
        if (existing) {
          existing.occurrences.push(occurrence);
          continue;
        }
        grouped.set(key, {
          key,
          trackName: track.name,
          trackIndex,
          name: clip.name,
          sourceFile: basename(clip.filePath),
          durationBeats: clip.duration,
          durationSec: beatsToSeconds(clip.duration, bpm),
          startMarker: clip.startMarker,
          endMarker: clip.endMarker,
          color: clip.color,
          muted: clip.muted,
          warped: clip.isWarped,
          warpMode: "",
          looping: clip.looping,
          ...(isMidi ? { kind: "midi" as const, noteCount: clip.notes?.length ?? 0 } : {}),
          occurrences: [occurrence],
        });
      }

      const entries = [...grouped.values()]
        .map((c) => ({ ...c, occurrences: [...c.occurrences].sort((a, b) => a.start - b.start) }))
        .sort((a, b) => (a.occurrences[0]?.start ?? 0) - (b.occurrences[0]?.start ?? 0));

      distinct += entries.length;
      placements += clips.length;
      tracks.push({
        trackIndex,
        name: track.name,
        playable: clips.every(canSound),
        clips: entries,
        placements: clips.length,
      });
    });

  return {
    tracks,
    distinctClips: distinct,
    totalPlacements: placements,
    unplayableTracks: unplayable,
  };
}
